import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { createPrimeContact, type PrimeContact } from "../api/CrmApi";
import { apiErrorMessage } from "../apiError";
import CompanyPicker, { type CompanyOption } from "./CompanyPicker";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  /** The company the contact joins. Leave it out to let the person choose one (the Prime Contacts page). */
  company?: { id: number; name: string | null };
  onClose: () => void;
  onSaved: (contact: PrimeContact) => void;
}

const LABEL = "block text-xs font-medium text-muted-foreground mb-1.5";
const INPUT = `w-full px-3 py-2 ${FORM_CONTROL}`;

interface Confirm { kind: "generic" | "other"; message: string }
interface InUse { message: string; prime_contact_id: number | null }
interface PrimaryClash { name: string; email: string }

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function AddPrimeContactModal({ company, onClose, onSaved }: Props) {
  const location = useLocation();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";
  const [picked, setPicked] = useState<CompanyOption | null>(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null); // the address is outside the company's own domain: ask first
  const [inUse, setInUse] = useState<InUse | null>(null);
  const [makePrimary, setMakePrimary] = useState(false);
  // Which confirmations the person has already given for this form, so confirming one never drops the other.
  const [confirmed, setConfirmed] = useState({ otherDomain: false, replacePrimary: false });
  const [primaryClash, setPrimaryClash] = useState<PrimaryClash | null>(null); // the company already has a primary: ask before replacing it

  const companyId = company?.id ?? picked?.value;
  const companyName = company ? company.name : picked?.label;
  const emailOk = EMAIL_SHAPE.test(email.trim());
  const canSave = !!companyId && emailOk && firstName.trim().length > 0 && !saving;

  function changed<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setConfirm(null);
      setInUse(null);
      setPrimaryClash(null);
      setConfirmed({ otherDomain: false, replacePrimary: false });
      setError(null);
    };
  }

  async function save(allowOtherDomain: boolean, replacePrimary: boolean) {
    if (!companyId) return;
    setConfirmed({ otherDomain: allowOtherDomain, replacePrimary });
    setSaving(true);
    setError(null);
    setConfirm(null);
    setInUse(null);
    setPrimaryClash(null);
    try {
      const created = await createPrimeContact(companyId, {
        email: email.trim(),
        first_name: firstName.trim(),
        ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(linkedin.trim() ? { personal_linkedin_url: linkedin.trim() } : {}),
        ...(allowOtherDomain ? { allow_other_domain: true } : {}),
        ...(makePrimary ? { is_primary: true } : {}),
        ...(makePrimary && replacePrimary ? { replace_primary: true } : {}),
      });
      onSaved(created);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "email_domain_mismatch") {
        setConfirm({ kind: detail.kind, message: detail.message });
      } else if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "primary_exists") {
        setPrimaryClash({ name: detail.name, email: detail.email });
      } else if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "email_in_use") {
        setInUse({ message: detail.message, prime_contact_id: detail.prime_contact_id ?? null });
      } else {
        setError(apiErrorMessage(err, "Could not add the contact. Please try again."));
      }
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="Add contact"
      description={companyName ? `Adds a person to ${companyName}` : "Adds a person to a company"}
      icon={<UserPlus />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => save(false, false)} disabled={!canSave || !!confirm || !!primaryClash}>
            {saving ? "Adding…" : "Add contact"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!company && (
          <div>
            <label htmlFor="add-contact-company" className={LABEL}>Company <span className="text-destructive">*</span></label>
            <CompanyPicker inputId="add-contact-company" value={picked} onChange={changed(setPicked)} autoFocus />
          </div>
        )}
        <div>
          <label htmlFor="add-contact-email" className={LABEL}>Email <span className="text-destructive">*</span></label>
          <input
            id="add-contact-email" type="text" value={email} onChange={(e) => changed(setEmail)(e.target.value)}
            placeholder="name@company.com" className={INPUT} autoFocus={!!company} data-testid="contact-email"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="add-contact-first-name" className={LABEL}>First name <span className="text-destructive">*</span></label>
            <input id="add-contact-first-name" type="text" value={firstName} onChange={(e) => changed(setFirstName)(e.target.value)} className={INPUT} data-testid="contact-first-name" />
          </div>
          <div>
            <label htmlFor="add-contact-last-name" className={LABEL}>Last name</label>
            <input id="add-contact-last-name" type="text" value={lastName} onChange={(e) => changed(setLastName)(e.target.value)} className={INPUT} />
          </div>
        </div>
        <div>
          <label htmlFor="add-contact-title" className={LABEL}>Title</label>
          <input id="add-contact-title" type="text" value={title} onChange={(e) => changed(setTitle)(e.target.value)} placeholder="e.g. Director of Procurement" className={INPUT} />
        </div>
        <div>
          <label htmlFor="add-contact-phone" className={LABEL}>Phone</label>
          <input id="add-contact-phone" type="text" value={phone} onChange={(e) => changed(setPhone)(e.target.value)} className={INPUT} />
        </div>
        <div>
          <label htmlFor="add-contact-linkedin" className={LABEL}>Personal LinkedIn URL</label>
          <input id="add-contact-linkedin" type="text" value={linkedin} onChange={(e) => changed(setLinkedin)(e.target.value)} placeholder="https://www.linkedin.com/in/..." className={INPUT} />
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" title="Outreach uses the primary contact first. A company has at most one.">
          <input
            type="checkbox" checked={makePrimary} onChange={(e) => changed(setMakePrimary)(e.target.checked)}
            className="accent-primary" data-testid="contact-make-primary"
          />
          Make this the company's primary contact
        </label>
        <p className="text-[11px] text-muted-foreground">
          The contact is saved as verified and is available to outreach from then on. It has no RFP yet — it shows under "No RFP yet" on the Prime Contacts page until a sheet brings it in.
        </p>
      </div>

      {confirm && (
        <Alert tone="warning" className="mt-4" role="alert">
          <p>{confirm.message}</p>
          <p className="mt-1">
            {confirm.kind === "generic"
              ? `Add it only if this is the person's personal or shared address and they really belong to ${companyName ?? "this company"}.`
              : `Add it only if this person really works for ${companyName ?? "this company"} despite the different domain.`}
          </p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => save(true, confirmed.replacePrimary)} disabled={saving}>
            {saving ? "Adding…" : "Add anyway"}
          </Button>
        </Alert>
      )}
      {primaryClash && (
        <Alert tone="warning" className="mt-4" role="alert">
          <p>{primaryClash.name} ({primaryClash.email}) is already {companyName ? `${companyName}'s` : "this company's"} primary contact.</p>
          <p className="mt-1">Replacing them removes that mark from them and makes the new contact primary.</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => save(confirmed.otherDomain, true)} disabled={saving}>
            {saving ? "Adding…" : "Replace and add"}
          </Button>
        </Alert>
      )}
      {inUse && (
        <Alert tone="danger" className="mt-4" role="alert">
          {inUse.message}{" "}
          {inUse.prime_contact_id && (
            <Link to={`${crmBasePath}/prime-contacts/${inUse.prime_contact_id}`} className="underline" onClick={onClose}>
              Open that contact
            </Link>
          )}
        </Alert>
      )}
      {error && <Alert tone="danger" className="mt-4" role="alert">{error}</Alert>}
    </Modal>
  );
}
